import React from 'react';
import './SubscriptionManager.css';
import { Button, Progress } from 'antd';
import { convertUTCToLocal } from 'utils/dateFormatter';
import { SUBSCRIPTION_STATUS } from 'utils/constants';
import { manageBilling } from 'assets/images';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from 'utils/routes';

/**
 * This component renders the active subscription details and statistics
 *
 * @return {ReactNode} A React element that renders the active subscription details and statistics
 */

const SubscriptionManager = ({
  activePlan,
  handleCloseManager,
  handleCancelPlan,
  handlePauseOrResumePlan,
}) => {
  const navigate = useNavigate();

  /**
   * This function is used to cancel the subscription of a store
   *
   * @returns {void}
   */

  const handleCancelSubscription = () => {
    handleCancelPlan();
  };

  /**
   * This function is used to pause or resume the subscription of a store
   *
   * @returns {void}
   */

  const handlePauseSubscription = () => {
    handlePauseOrResumePlan();
  };

  /**
   * This function is used to get the text of the pause subscription button
   *
   * @returns {String} Text to be displayed on the button
   */

  const getSubscriptionStatusText = () => {
    if (activePlan?.subscription_status === SUBSCRIPTION_STATUS.PAUSED) {
      return 'Resume Subscription';
    }
    return 'Pause Subscription';
  };

  /**
   * This function is used to get the progress bar percentage for the subscription status
   *
   * @returns {Number} TSubscription status progress percentage
   */

  const getProgessBarPercentage = () => {
    if (isUnlimited()) return 0; // or undefined if you want empty bar
    return Math.round(
      (activePlan.calls_scheduled / activePlan?.call_limit) * 100
    );
  };

  /**
   * This function is used to navigate the store admin to the manage billing section
   *
   * @returns {void}
   */

  const handleManageBilling = () => {
    navigate(`${ROUTES.ADMIN_SETTING}?page=adminDots`);
  };

  // Unlimited meeting allowances are stored as this sentinel, because the
  // column cannot hold null. Entitlements carry the real answer as null.
  const INT_MAX = 2147483647;

  const isUnlimited = () => {
    return activePlan?.call_limit >= INT_MAX;
  };

  const carriedForward = activePlan?.carried_forward_calls || 0;

  // Billed terms come from the record's resolved entitlements, not the plan row.
  // A custom plan's row is shared across every bespoke customer and carries no
  // real price, so reading it here would show the wrong figures to exactly the
  // customers whose pricing was negotiated. Falls back to the plan for records
  // written before entitlements existed.
  const monthlyFee =
    activePlan?.entitlements?.monthly_fees ??
    activePlan?.subscription_type?.monthly_fees;
  const partnershipRate =
    activePlan?.entitlements?.prosperity_partnership ??
    activePlan?.subscription_type?.prosperity_partnership;

  // Host usage comes from the record's usage block, which counts hosts holding
  // at least one active product for the store.
  const hostsUsed = activePlan?.usage?.hosts_used || 0;
  const hostLimit = activePlan?.usage?.hosts_limit;
  const hostsUnlimited = hostLimit === null || hostLimit === undefined;

  /**
   * This function is used to get the progress bar percentage for host usage
   *
   * @returns {Number} Host usage progress percentage
   */

  const getHostProgressPercentage = () => {
    if (hostsUnlimited || !hostLimit) return 0;
    return Math.round((hostsUsed / hostLimit) * 100);
  };

  return (
    <div className="plan-manager-container">
      <div className="sub-plan-header">
        <div className="sub-plan-titles">
          <div className="sub-plan-main-header">Plan & Billing</div>
          <div className="sub-plan-sub-header">
            Manage your plan and payments
          </div>
        </div>
        <div className="sub-plan-manage-buttons">
          <Button
            className="sub-plan-action-btn"
            onClick={handlePauseSubscription}
          >
            {getSubscriptionStatusText()}
          </Button>
          <Button
            className="sub-plan-billing-btn"
            onClick={handleManageBilling}
          >
            Manage Billing{' '}
            <img
              className="manage-billing-icon"
              src={manageBilling}
              alt="Manage Billing"
            />
          </Button>
        </div>
      </div>
      <div className="sub-plan-details">
        <div className="sub-plan-info">
          <div className="sub-plan-info-header">Current plan</div>
          <div className="sub-plan-info-details">
            {activePlan?.subscription_type?.name || '-'} - ${monthlyFee}/Month &{' '}
            {partnershipRate}% of sales
          </div>
        </div>
        <div className="sub-plan-statistics">
          <div className="sub-plan-statistics-header">Renews</div>
          <div className="sub-plan-statistics-details">
            {convertUTCToLocal(activePlan?.expires_at)[0]}
          </div>
        </div>
      </div>
      <div className="sub-plan-usage">
        <div className="sub-plan-usage-header">Usage</div>
        <div className="sub-plan-usage-calls">
          Meetings ({activePlan.calls_scheduled}/
          {isUnlimited() ? '∞' : activePlan?.call_limit})
          {carriedForward > 0 && (
            <span className="sub-plan-usage-note">
              {' '}
              (includes {carriedForward} carried over from your previous plan)
            </span>
          )}
        </div>
        <div className="progress-bar-container">
          <Progress
            percent={getProgessBarPercentage()}
            strokeColor={'#23265F'}
            size={['100%', 15]}
            showInfo={false}
          />
        </div>
        <div className="sub-plan-usage-calls">
          Hosts ({hostsUsed}/{hostsUnlimited ? '∞' : hostLimit})
        </div>
        <div className="progress-bar-container">
          <Progress
            percent={getHostProgressPercentage()}
            strokeColor={'#23265F'}
            size={['100%', 15]}
            showInfo={false}
          />
        </div>
      </div>
      <div className="cancel-sub-buttons">
        <Button className="cancel-manage-btn" onClick={handleCloseManager}>
          Cancel
        </Button>
        <Button
          className="cancel-subscription-btn"
          onClick={handleCancelSubscription}
        >
          Cancel Subscription
        </Button>
      </div>
    </div>
  );
};

export default SubscriptionManager;
